import {
  getPrimaryImage,
  hasInstagramImage,
  isCarouselContent,
  stripDuplicateTrailingHashtags,
} from '@mondaygpt/constants'
import { Bookmark, Copy, Heart, MessageCircle, Send, Sparkles } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useCallback, useMemo } from 'react'
import { selectBrandLogo, useBrandKitStore } from '../store/brand-kit-store'
import {
  useActiveEditingSectionStore,
  useEditorFocusStore,
  useSectionContextStore,
} from '../store/conversation-store'
import type { InstagramPostContent } from '../types'
import { carouselSlideFocusKey, parseCarouselSlideFocus } from '../utils/carousel-slide-focus'
import type { MobileSelectedSection } from '../components/mobile-selection-types'
import { MobileSectionSelectButton } from '../components/MobileSectionSelectButton'
import { EditsApplyingBadge } from '../components/EditsApplyingBadge'
import { GeneratingMediaOverlay } from '../components/AssetMedia'
import { PreviewVideo } from './PreviewVideo'
import { FeedPreviewReelFallback } from './FeedPreviewReelFallback'
import { CarouselMediaPreview } from './CarouselMediaPreview'
import { InstagramPostSkeletonPreview } from './InstagramPostSkeletonPreview'
import {
  getFeedVideoPreviewState,
  imageAspectStyle,
  isSocialPostEmpty,
  toSocialHandle,
} from './preview-utils'
import { SectionHoverWrapper } from './SectionHoverWrapper'
import { EditableInlineText } from './EditableInlineText'
import { EditableHashtags } from './EditableHashtags'
import { SectionActionBar } from './SectionActionBar'
import { CanvasMediaWithBar, useHasComposerCanvas } from '../composition/editor/ComposerCanvasMedia'
import { VideoBeatCanvas } from '../composition/editor/VideoTimeline'
import { useCarouselDeck, useVideoTimeline } from '../composition/editor/CompositionEditorProvider'
import { useCompositionLoading } from '../composition/editor/use-composition-loading'
import { useIsMobile } from 'hooks/use-mobile'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useCreativeStore } from '../store/creative-store'

// Accent tint for skeleton placeholders — matches the editor selection chrome.
// Built on `var(--acid)` (lime on dark, Pool cyan on user-light) via color-mix
// so the shimmer follows the active theme instead of pinning to lime.
const SKEL = 'color-mix(in srgb, var(--acid) 12%, transparent)'
const SKEL_BG =
  'linear-gradient(110deg, color-mix(in srgb, var(--acid) 4%, transparent) 8%, color-mix(in srgb, var(--acid) 10%, transparent) 18%, color-mix(in srgb, var(--acid) 4%, transparent) 33%)'

// ─── Preview Component ──────────────────────────────────────────────────────

interface InstagramPostPreviewProps {
  content: InstagramPostContent
  /** Set of content fields currently being generated (e.g. 'image', 'caption', 'hashtags') */
  generatingFields?: Set<string>
  /** Creative ID for analytics tracking */
  creativeId?: string
  /** Post-scoped key for shared mute state. See PreviewVideo. */
  postKey: string
  /** Mobile section selection */
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  /** When false, the live canvas renders read-only and tapping the image fires onSectionSelect
   *  (mobile preview-first home drills into the editor). Defaults to true. */
  interactive?: boolean
  /**
   * "Feed-card" rendering used inside PostModal: drops the bottom timestamp
   * and adds the post-image action row (Heart / MessageCircle / Send +
   * Bookmark) so the preview reads like the in-feed Instagram post the user
   * is about to publish, not the editor surface.
   */
  compact?: boolean
  /**
   * When provided (a LIVE post), renders the real engagement under the action
   * row — "{likes} likes" + "View all N comments" — so the preview reads like
   * the published post on Instagram, not a draft. Only shown in `compact`.
   */
  engagement?: { likeCount: number; commentsCount: number }
}

export function InstagramPostPreview({
  content,
  generatingFields,
  creativeId,
  postKey,
  selectedSectionId,
  onSectionSelect,
  interactive = true,
  compact = false,
  engagement,
}: InstagramPostPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const logoUrl = useBrandKitStore(selectBrandLogo)
  const businessName = business?.name || 'Your Business'
  const setContext = useSectionContextStore(s => s.setContext)
  const requestFocus = useEditorFocusStore(s => s.requestFocus)
  const activeField = useActiveEditingSectionStore(s =>
    creativeId ? s.activeSections[creativeId] : null
  )
  const activeEditingSlide = useMemo(() => parseCarouselSlideFocus(activeField), [activeField])

  const initials = businessName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  const carouselItems = content.mediaItems ?? []
  const isCarousel = isCarouselContent(content)
  const hasImage = hasInstagramImage(content)
  // When the active post has an editable Composition (provided by the composer), the
  // media region becomes the live canvas — ambient editing, no mode switch.
  const hasCanvas = useHasComposerCanvas()
  // A carousel with an editable deck composition → the editable deck editor (filmstrip + active
  // slide canvas). Falls back to the read-only CarouselMediaPreview for legacy decks / mobile.
  const deck = useCarouselDeck()
  const canvasCarousel = isCarousel && !!deck && interactive
  // A reel with an editable video Composition → the editable timeline (beats + per-beat overlay
  // editor). Falls back to the read-only PreviewVideo for legacy reels / mobile / non-interactive.
  const timeline = useVideoTimeline()
  const canvasTimeline = !!timeline && interactive
  // While the composition is still being fetched, suppress the legacy hover so it doesn't flash
  // before the live canvas mounts in its place.
  const compLoading = useCompositionLoading(creativeId)
  // Ambient inline editing for caption + hashtags (desktop only; mobile keeps its tap→sheet
  // flow). updateCreativeContent persists (debounced PUT) and the server re-scores.
  const isMobile = useIsMobile()
  const readOnly = useComposerReadOnly()
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const editable = !!creativeId && !readOnly && !isMobile
  const primaryImageUrl = getPrimaryImage(content).url ?? undefined
  const hasReadyVideo = !!content.videoUrl
  const hasVideo = hasReadyVideo
  const hashtags = content.hashtags ?? []
  const caption = stripDuplicateTrailingHashtags(content.caption, content.hashtags)
  const hasCaption = !!caption
  const hasHashtags = hashtags.length > 0
  // Size the single-image frame to the real image ratio when known.
  const imageStyle = imageAspectStyle(content.imageRatio)

  const genVideo = generatingFields?.has('video') ?? false
  const genImage = generatingFields?.has('image') ?? false
  const genCaption = generatingFields?.has('caption') ?? false
  const genHashtags = generatingFields?.has('hashtags') ?? false
  const isGenerating = (generatingFields?.size ?? 0) > 0
  const { isGeneratingVideo, isFailedReel, isVideoMode, showCardOverlay } =
    getFeedVideoPreviewState(content, isGenerating, primaryImageUrl)

  // ─── Mobile section selection ──────────────────────────────────────────────
  const handleSectionSelect = useCallback(
    (sectionType: string, label: string, preview: string) => {
      if (!onSectionSelect || !creativeId) return
      if (selectedSectionId === sectionType) {
        onSectionSelect(null)
      } else {
        onSectionSelect({
          sectionId: sectionType,
          sectionType,
          creativeType: 'instagram_post',
          label,
          preview,
        })
      }
    },
    [creativeId, selectedSectionId, onSectionSelect]
  )

  // ─── Ask AI (send section context to chat) ────────────────────────────────
  const handleAskAI = useCallback(
    (field: string, label: string, preview: string) => {
      if (creativeId) {
        setContext(creativeId, {
          sectionId: field,
          sectionType: field,
          label,
          preview,
        })
      }
    },
    [creativeId, setContext]
  )

  // ─── Edit (focus section in editor panel) ─────────────────────────────────
  const handleEdit = useCallback(
    (field: string) => {
      if (creativeId) {
        requestFocus(creativeId, field)
      }
    },
    [creativeId, requestFocus]
  )

  if (isSocialPostEmpty(content)) {
    return <InstagramPostSkeletonPreview />
  }

  // Reels render the media at a fixed 9:16 / 320px phone-frame. If the card
  // stayed at its post-width (max-w-md), the header avatar would sit ~100px
  // left of the video and the chrome would look like a void around the
  // video. Shrink the card to match the video frame so header padding lines
  // up with the video's left edge.

  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-2xl bg-white transition-all duration-300 ${isVideoMode ? 'max-w-[320px]' : 'max-w-md'}`}
      style={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {showCardOverlay && (
        <GeneratingMediaOverlay
          shape={isCarousel ? 'carousel' : isVideoMode ? 'video' : 'post'}
          tone="light"
          radius={16}
        />
      )}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={businessName}
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-[10px] font-bold text-white">
            {initials}
          </div>
        )}
        <span className="text-sm font-semibold text-gray-900">{toSocialHandle(businessName)}</span>
      </div>

      {/* Media (Video or Image) — 9:16 phone-frame for Reels, 1:1 for posts. When an
          editable Composition exists for a single-image post, the media IS the live
          canvas (the post's real aspect comes from the composition). */}
      {canvasTimeline ? (
        <VideoBeatCanvas onTap={() => handleSectionSelect('image', 'Video', 'Reel')} />
      ) : hasCanvas && hasImage && !isVideoMode && !isCarousel ? (
        <CanvasMediaWithBar
          clipClassName="w-full overflow-hidden"
          interactive={interactive}
          onTap={() => handleSectionSelect('image', 'Image', 'Post media')}
        />
      ) : canvasCarousel ? (
        <CanvasMediaWithBar
          clipClassName="w-full overflow-hidden"
          onTap={() => handleSectionSelect('image', 'Carousel', `${carouselItems.length} slides`)}
        />
      ) : (
        <div
          className={`relative cursor-pointer overflow-hidden transition-all duration-200 md:cursor-default ${
            isCarousel
              ? 'w-full'
              : isVideoMode
                ? 'aspect-[9/16] w-full bg-black'
                : `${imageStyle ? '' : 'aspect-square'} w-full`
          } ${selectedSectionId === 'image' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          style={!isCarousel && !isVideoMode ? imageStyle : undefined}
          onClick={() => onSectionSelect && handleSectionSelect('image', 'Image', 'Post media')}
        >
          {isCarousel ? (
            <>
              <CarouselMediaPreview
                items={carouselItems}
                altText={content.altText}
                {...(creativeId
                  ? {
                      onAskAISlide: (idx: number) =>
                        handleAskAI(
                          carouselSlideFocusKey(idx),
                          `Slide ${idx + 1} of ${carouselItems.length}`,
                          carouselItems[idx]?.altText || `Image ${idx + 1}`
                        ),
                      onEditSlide: (idx: number) => handleEdit(carouselSlideFocusKey(idx)),
                      activeEditingSlide,
                    }
                  : {})}
              />
              {activeField === 'image' && (
                <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() =>
                    handleSectionSelect('image', 'Carousel', `${carouselItems.length} slides`)
                  }
                />
              )}
            </>
          ) : hasVideo ? (
            <>
              {!genVideo && creativeId ? (
                <SectionHoverWrapper
                  fieldId="video"
                  onAskAI={() => handleAskAI('video', 'Video', 'Post video')}
                  onEdit={() => handleEdit('video')}
                  isActiveEditing={activeField === 'video'}
                >
                  <PreviewVideo
                    src={content.videoUrl ?? ''}
                    postKey={postKey}
                    className="h-full w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <PreviewVideo
                  src={content.videoUrl ?? ''}
                  postKey={postKey}
                  className="h-full w-full object-cover"
                />
              )}
              {!genVideo && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Video', 'Post video')}
                />
              )}
            </>
          ) : isFailedReel || (isGeneratingVideo && primaryImageUrl) ? (
            <FeedPreviewReelFallback
              posterUrl={primaryImageUrl}
              isFailedReel={isFailedReel}
              isGeneratingVideo={isGeneratingVideo}
            />
          ) : hasImage ? (
            <>
              {!genImage && creativeId && !compLoading ? (
                <SectionHoverWrapper
                  fieldId="image"
                  onAskAI={() => handleAskAI('image', 'Image', 'Post image')}
                  onEdit={() => handleEdit('image')}
                  isActiveEditing={activeField === 'image'}
                >
                  <img
                    src={primaryImageUrl}
                    alt={content.altText || 'Post image'}
                    className="h-full w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <img
                  src={content.imageUrl ?? undefined}
                  alt={content.altText || 'Post image'}
                  className="h-full w-full object-cover"
                />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Image', 'Post image')}
                />
              )}
            </>
          ) : (
            <div
              className={`flex h-full w-full flex-col items-center justify-center gap-3 ${genImage || genVideo ? '' : 'bg-gray-50'}`}
              style={genImage || genVideo ? { background: SKEL_BG } : undefined}
            >
              <div
                className={`z-[1] flex h-[72px] w-[72px] items-center justify-center rounded-2xl ${genImage || genVideo ? '' : 'bg-gray-200'}`}
                style={genImage || genVideo ? { background: SKEL } : undefined}
              >
                <span className="text-3xl">{genVideo ? '🎬' : genImage ? '🎨' : '🖼️'}</span>
              </div>
              <div
                className={`z-[1] h-3 w-[140px] rounded-md ${genImage || genVideo ? '' : 'bg-gray-200'}`}
                style={genImage || genVideo ? { background: SKEL } : undefined}
              />
            </div>
          )}
          {/* Master-image fallback only (never the live canvas branches above):
            the rendered master lags the user's composition edits until the
            server re-renders it — say so instead of showing a silent revert.
            Video branches excluded (the flag is never set for video posts). */}
          {content.compositionPendingRender &&
            !isGenerating &&
            !isVideoMode &&
            !hasVideo &&
            (isCarousel || hasImage) && <EditsApplyingBadge overlay creativeId={creativeId} />}
        </div>
      )}

      {/* Feed-style action row (compact / PostModal only). Real Instagram
          renders these as the post-image affordances; in the editor surface
          they'd compete with the section-edit chrome, so they're gated. */}
      {compact && (
        <div className="flex items-center gap-4 px-4 pt-3 text-gray-900">
          <Heart size={22} strokeWidth={1.75} />
          <MessageCircle size={22} strokeWidth={1.75} />
          <Send size={22} strokeWidth={1.75} />
          <Bookmark size={22} strokeWidth={1.75} className="ml-auto" />
        </div>
      )}

      {/* Live engagement — makes the preview read like the published post.
          Zero counts are hidden (Instagram doesn't show "0 likes"). */}
      {compact && engagement && (engagement.likeCount > 0 || engagement.commentsCount > 0) && (
        <div className="px-4 pt-2 text-[13px] leading-snug text-gray-900">
          {engagement.likeCount > 0 && (
            <div className="font-semibold">{engagement.likeCount.toLocaleString()} likes</div>
          )}
          {engagement.commentsCount > 0 && (
            <div className="text-gray-500">
              View all {engagement.commentsCount.toLocaleString()} comments
            </div>
          )}
        </div>
      )}

      {/* Caption — inline-editable in place on desktop (click → caret); mobile keeps its
          tap→sheet flow. The hover Ask-AI/Edit buttons are gone — AI lives in the chat. */}
      {editable && !genCaption ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(content.caption ?? '')
                  .catch(err => console.error('copy caption failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () =>
                handleAskAI('caption', 'Caption', (content.caption ?? '').slice(0, 80)),
            },
          ]}
        >
          <div className="px-4 pt-3 pb-2">
            <p className="text-sm text-gray-900">
              <span className="mr-1 font-semibold">{toSocialHandle(businessName)}</span>
              <EditableInlineText
                initialValue={caption}
                onCommit={t => updateCreativeContent(creativeId!, { caption: t })}
                ariaLabel="Edit caption"
                placeholder="Write a caption…"
                className="text-sm text-gray-900 outline-none focus:outline-none"
              />
            </p>
          </div>
        </SectionActionBar>
      ) : hasCaption ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'caption' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect &&
            handleSectionSelect('caption', 'Caption', content.caption.slice(0, 80))
          }
        >
          <div className="relative overflow-hidden px-4 pt-3 pb-2">
            {activeField === 'caption' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
            )}
            <p className="text-sm text-gray-900">
              <span className="mr-1 font-semibold">{toSocialHandle(businessName)}</span>
              {caption}
            </p>
          </div>
          {!genCaption && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'caption'}
              onSelect={() =>
                handleSectionSelect('caption', 'Caption', content.caption.slice(0, 80))
              }
            />
          )}
        </div>
      ) : genCaption ? (
        <div className="relative overflow-hidden px-4 pt-3 pb-2">
          <div className="flex flex-col gap-2.5">
            <div className="flex gap-2">
              <div className="h-2.5 w-20 rounded-md" style={{ background: SKEL }} />
              <div className="h-2.5 w-40 rounded-md" style={{ background: SKEL }} />
            </div>
            <div className="h-2.5 w-32 rounded-md" style={{ background: SKEL }} />
          </div>
        </div>
      ) : (
        <div className="px-4 pt-3 pb-2">
          <p className="text-sm text-gray-400 italic">Caption will appear here...</p>
        </div>
      )}

      {/* Hashtags — editable chips on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genHashtags ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(hashtags.map(h => `#${h}`).join(' '))
                  .catch(err => console.error('copy hashtags failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () =>
                handleAskAI('hashtags', 'Hashtags', hashtags.map(h => `#${h}`).join(' ')),
            },
          ]}
        >
          <div className="px-4 pb-2">
            <EditableHashtags
              value={hashtags}
              onCommit={h => updateCreativeContent(creativeId!, { hashtags: h })}
              className="text-[#1FA1FF]"
            />
          </div>
        </SectionActionBar>
      ) : hasHashtags ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'hashtags' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect &&
            handleSectionSelect(
              'hashtags',
              'Hashtags',
              content.hashtags.map(h => `#${h}`).join(' ')
            )
          }
        >
          <div className="relative overflow-hidden px-4 pb-2">
            {activeField === 'hashtags' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
            )}
            <p className="text-sm text-[#1FA1FF]">{content.hashtags.map(h => `#${h}`).join(' ')}</p>
          </div>
          {!genHashtags && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'hashtags'}
              onSelect={() =>
                handleSectionSelect(
                  'hashtags',
                  'Hashtags',
                  content.hashtags.map(h => `#${h}`).join(' ')
                )
              }
            />
          )}
        </div>
      ) : genHashtags ? (
        <div className="relative overflow-hidden px-4 pb-2">
          <div className="h-2.5 w-24 rounded-md" style={{ background: SKEL }} />
        </div>
      ) : null}

      {/* Timestamp — editor-surface only. PostModal omits this so the preview
          reads as a feed card, not a metadata row. */}
      {!compact && (
        <div className="border-t px-4 py-2">
          <p className="text-[10px] tracking-wider text-gray-400 uppercase">Just now</p>
        </div>
      )}
    </div>
  )
}
